import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAdminEnrollments, deleteEnrollment } from '../../api/enrollments';
import { getAdminEditions, exportEnrollments } from '../../api/courses';
import StatusBadge from '../../components/StatusBadge';
import type { EnrollmentStatus } from '../../types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const ALL_STATUSES: EnrollmentStatus[] = [
  'waitlisted',
  'portal_registered',
  'lms_enrolled',
  'zoom_registration_pending',
  'zoom_registered',
  'zoom_approved',
  'join_link_sent',
  'attended',
  'no_show',
  'cancelled',
];

const STATUS_LABELS: Record<EnrollmentStatus, string> = {
  waitlisted: 'Lista d\'attesa',
  portal_registered: 'Iscrizione ricevuta',
  lms_enrolled: 'LMS confermata',
  zoom_registration_pending: 'Zoom in attesa',
  zoom_registered: 'Zoom registrato',
  zoom_approved: 'Approvato',
  join_link_sent: 'Link inviato',
  attended: 'Presente',
  no_show: 'Non presente',
  cancelled: 'Annullato',
};

export default function AdminEnrollmentsPage() {
  const [editionFilter, setEditionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const queryClient = useQueryClient();

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Eliminare l'iscrizione di ${name}? L'operazione non è reversibile.`)) return;
    try {
      await deleteEnrollment(id);
      queryClient.invalidateQueries({ queryKey: ['admin-enrollments'] });
    } catch {
      alert('Errore durante l\'eliminazione. Riprova.');
    }
  };

  const { data: editions = [] } = useQuery({
    queryKey: ['admin-editions'],
    queryFn: getAdminEditions,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-enrollments', editionFilter, statusFilter, search, page],
    queryFn: () =>
      getAdminEnrollments({
        edition_id: editionFilter || undefined,
        status: statusFilter || undefined,
        search: search || undefined,
        page,
        limit: 20,
      }),
    keepPreviousData: true,
  } as any);

  const enrollments = (data as any)?.enrollments ?? [];
  const pagination = (data as any)?.pagination;

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  }, []);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Iscrizioni</h1>
        <button onClick={() => refetch()} className="btn btn-ghost btn-sm">
          Aggiorna
        </button>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <input
          type="text"
          className="form-control"
          placeholder="Cerca per nome, email..."
          value={search}
          onChange={handleSearchChange}
          style={{ minWidth: '220px' }}
        />

        <select
          className="form-control"
          value={editionFilter}
          onChange={(e) => { setEditionFilter(e.target.value); setPage(1); }}
        >
          <option value="">Tutte le edizioni</option>
          {editions.map((ed) => (
            <option key={ed.id} value={ed.id}>
              {ed.course?.name} — {ed.label}
            </option>
          ))}
        </select>

        <select
          className="form-control"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">Tutti gli stati</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>

        {editionFilter && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => exportEnrollments(editionFilter).catch(() => alert('Errore durante l\'esportazione'))}
          >
            ⬇ Esporta CSV
          </button>
        )}

        {(editionFilter || statusFilter || search) && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setEditionFilter('');
              setStatusFilter('');
              setSearch('');
              setPage(1);
            }}
          >
            Azzera filtri
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        {isLoading ? (
          <div className="loading-container">
            <div className="spinner" />
          </div>
        ) : enrollments.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--gray-500)' }}>
            Nessuna iscrizione trovata
          </div>
        ) : (
          <>
            <div className="table-container" style={{ borderRadius: 0, border: 'none' }}>
              <table>
                <thead>
                  <tr>
                    <th>Partecipante</th>
                    <th>Azienda</th>
                    <th>Corso / Edizione</th>
                    <th>Stato</th>
                    <th>Iscritto il</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollments.map((enrollment: any) => (
                    <tr key={enrollment.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>
                          {enrollment.user?.nome} {enrollment.user?.cognome}
                        </div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>
                          {enrollment.user?.email}
                        </div>
                      </td>
                      <td style={{ color: 'var(--gray-600)' }}>
                        {enrollment.user?.azienda ?? '—'}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                          {enrollment.edition?.course?.name ?? '—'}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                          {enrollment.edition?.label}
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={enrollment.status as EnrollmentStatus} />
                      </td>
                      <td style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                        {format(new Date(enrollment.registeredAt), 'd MMM yyyy', { locale: it })}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Link
                            to={`/admin/enrollments/${enrollment.id}`}
                            className="btn btn-ghost btn-sm"
                          >
                            Dettaglio
                          </Link>
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ color: 'var(--danger)' }}
                            onClick={() => handleDelete(
                              enrollment.id,
                              `${enrollment.user?.nome} ${enrollment.user?.cognome}`
                            )}
                          >
                            Elimina
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.total > 20 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem 1.5rem',
                  borderTop: '1px solid var(--gray-100)',
                }}
              >
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                  {enrollments.length} risultati
                </span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Precedente
                  </button>
                  <span
                    style={{
                      padding: '0.25rem 0.75rem',
                      fontSize: '0.875rem',
                      color: 'var(--gray-600)',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    Pagina {page}
                  </span>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={enrollments.length < 20}
                  >
                    Successiva
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
